/**
 * AES-256-GCM envelope for cloak telemetry payloads (beacon / events / probe /
 * verify request bodies). Shared verbatim by the three runtimes that all expose
 * the same Web Crypto API: the landing client bundle (browser), the inline
 * <head> beacon (browser — logic mirrored by hand in index.html), and the
 * worker (workerd).
 *
 * Wire layout (binary, sent as application/octet-stream):
 *
 *     byte 0       version / key id  (ENVELOPE_VERSION)
 *     bytes 1..12  random 96-bit IV
 *     bytes 13..   AES-GCM ciphertext with the 128-bit auth tag appended
 *
 * The symmetric key ships inside the client bundle, so this is obfuscation-grade
 * by design (an accepted trade-off): it stops abuse / ad-review tooling from
 * reading or keyword-matching the plaintext signal payload — not a motivated
 * reverse engineer. Rotate by generating a new key, bumping ENVELOPE_VERSION,
 * and updating BEACON_KEY on the worker and VITE_BEACON_KEY on the landing build
 * (both must hold the same value).
 */

/** Envelope format version; doubles as a key id so the worker can tell which
 *  key a ciphertext was sealed with after a rotation. */
export const ENVELOPE_VERSION = 0x01;

const IV_BYTES = 12;
const HEADER_BYTES = 1 + IV_BYTES;

/** Built-in symmetric telemetry key (base64, 32 bytes / AES-256). Overridable at
 *  build/deploy time via BEACON_KEY (worker) and VITE_BEACON_KEY (landing) — the
 *  two must always match. Keep in sync with the inline default in
 *  apps/landing/vite.config.ts. */
export const TELEMETRY_KEY = 'bILz/mhJg8v5n/s7hkIPyTJVsX2JE4Dx3sYAMOavlno=';

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Import a base64 AES key for use with {@link encryptEnvelope} /
 *  {@link decryptEnvelope}. Callers cache the resolved key per runtime. */
export function importTelemetryKey(base64Key: string): Promise<CryptoKey> {
  // `as BufferSource`: TS 5.7's Uint8Array<ArrayBufferLike> isn't assignable to
  // the WebCrypto BufferSource param (which excludes SharedArrayBuffer); the
  // bytes are always ArrayBuffer-backed here.
  return crypto.subtle.importKey(
    'raw',
    base64ToBytes(base64Key) as BufferSource,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Seal a plaintext string into the binary envelope (fresh IV every call). */
export async function encryptEnvelope(
  key: CryptoKey,
  plaintext: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const cipher = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext)),
  );
  const out = new Uint8Array(HEADER_BYTES + cipher.length);
  out[0] = ENVELOPE_VERSION;
  out.set(iv, 1);
  out.set(cipher, HEADER_BYTES);
  return out;
}

/** Open an envelope back into its plaintext string. Throws if the bytes are not
 *  a valid, untampered envelope (GCM auth tag mismatch rejects). */
export async function decryptEnvelope(key: CryptoKey, data: Uint8Array): Promise<string> {
  if (data.length <= HEADER_BYTES || data[0] !== ENVELOPE_VERSION) {
    throw new Error('not a telemetry envelope');
  }
  const iv = data.subarray(1, HEADER_BYTES);
  const cipher = data.subarray(HEADER_BYTES);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    cipher as BufferSource,
  );
  return new TextDecoder().decode(plain);
}

/** Cheap discriminator so the worker can accept both wire formats during a
 *  rollout / rollback: our envelope starts with the version byte (0x01), whereas
 *  a plaintext JSON body starts with '{' (0x7b), '[' (0x5b) or ASCII whitespace. */
export function looksLikeEnvelope(data: Uint8Array): boolean {
  return data.length > HEADER_BYTES && data[0] === ENVELOPE_VERSION;
}
